import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import { getHomepageSettings } from '../../../services/contentApi';

const CTASection = () => {
  const [ctaData, setCtaData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getHomepageSettings();
        if (data?.cta) {
          setCtaData(data.cta);
        }
      } catch {
        // silent fallback
      }
    })();
  }, []);

  return (
    <section className="py-16 bg-gradient-to-br from-primary via-primary/95 to-primary/90 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 blueprint-grid opacity-10"></div>
      
      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-[#884a0f]/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-16 h-16 bg-accent/20 rounded-full blur-xl animate-pulse delay-1000"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/10 shadow-sm">
            <Icon name="Rocket" size={16} className="text-[#884a0f]" />
            <span className="text-white text-sm font-medium">{ctaData?.badge || 'Ready to Transform Your Project?'}</span>
          </div>

          {/* Main Headline */}
          <h2 className="font-headline text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            {ctaData?.headlineLine1 || 'Start Your Construction Journey with'}
            <span className="text-[#884a0f] block mt-2">{ctaData?.headlineLine2 || 'Confidence Today'}</span>
          </h2>

          {/* Subheadline */}
          <p className="font-body text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {ctaData?.subheadline || 'Join 500+ successful projects in Pune. Get expert guidance, smart tools, and regulatory support all in one platform.'}
          </p>

          {/* Value Propositions - Glass Effect */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 shadow-lg border border-white/20 transform transition-transform hover:-translate-y-1">
              <Icon name="Clock" size={32} className="text-[#884a0f] mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">Save Time</h3>
              <p className="text-white/80 text-sm">Reduce approval time from 6 months to 45 days</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 shadow-lg border border-white/20 transform transition-transform hover:-translate-y-1">
              <Icon name="Shield" size={32} className="text-[#884a0f] mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">Ensure Compliance</h3>
              <p className="text-white/80 text-sm">100% regulatory compliance guaranteed</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 shadow-lg border border-white/20 transform transition-transform hover:-translate-y-1">
              <Icon name="TrendingUp" size={32} className="text-[#884a0f] mx-auto mb-4" />
              <h3 className="font-semibold text-white mb-2">Maximize ROI</h3>
              <p className="text-white/80 text-sm">Average cost savings of ₹15+ lakhs per project</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              variant="default" 
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="text-lg px-8 py-4 bg-white text-primary hover:bg-gray-100 shadow-md"
              onClick={() => window.dispatchEvent(new Event('open-consultation-modal'))}
            >
              {ctaData?.buttonPrimary || 'Book Free Consultation'}
            </Button>
            <Link to="/projects">
              <Button 
                variant="outline" 
                size="lg"
                iconName="Building"
                iconPosition="left"
                className="text-lg px-8 py-4 border-white text-white hover:bg-white/10"
              >
                {ctaData?.buttonSecondary || 'View Our Projects'}
              </Button>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-white/80 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} />
              <span>Government Registered</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={16} />
              <span>ISO Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={16} />
              <span>CREDAI Member</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} />
              <span>PMC Approved</span>
            </div>
          </div>
        </div>

        {/* Bottom Visual Element - Glass Effect */}
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="relative">
            <div className="bg-white/10 backdrop-blur-md shadow-xl rounded-2xl p-8 text-center border border-white/20">
              <div className="grid md:grid-cols-3 gap-6 text-white">
                <div>
                  <div className="text-3xl font-bold mb-2 text-[#884a0f]">15</div>
                  <div className="text-sm opacity-90">Days Average Approval</div>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2 text-[#884a0f]">₹2Cr+</div>
                  <div className="text-sm opacity-90">Total Client Savings</div>
                </div>
                <div>
                  <div className="text-3xl font-bold mb-2 text-[#884a0f]">98%</div>
                  <div className="text-sm opacity-90">Success Rate</div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -left-4 w-8 h-8 border-2 border-white/40 rounded-full"></div>
            <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-[#884a0f]/80 rounded-full"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;